
import * as React from "react";
import { gql, useMutation } from "@apollo/client";
import { Context } from "../Context/Context.js";
import Carousel from '../CommonComponents/Carousel.react.js';
import { useRef } from "react";
import GetCurrencySymbol from '../utils/GetCurrencySymbol.js';
import {
  Text,
  Button,
  Badge,
  Spinner
} from '@shopify/polaris';
import Box from '../CommonComponents/Box.react.js';
import DeferredMediaImage from '../CommonComponents/DeferredMediaImage.react.js';
import { useAppBridge } from '@shopify/app-bridge-react';
import { i18 } from "./ProductSearchUtil.js";
import { useNavigate } from "react-router-dom";
import { openExternalUrl } from '../utils/urlUtils.js';

const ADD_PRODUCT_LIST_FROM_URL = gql`
  mutation Mutation($aliExpressProductURL: String) {
    addProductListFormURL(aliExpressProductURL: $aliExpressProductURL) {
        status
        message
        showRatingModal
        errorMessage
    }
  }
`;
function parseImageList(images) {
  if (Array.isArray(images)) return images;
  if (typeof images !== "string" || images.trim() === "") return [];
  try {
    const parsed = JSON.parse(images);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

export default function ProductCard(props) {
  const {
    cardWidth,
    title,
    numberSold,
    id,
    isSaved,
    images,
    videoLink,
    minSalePrice,
    currency,
    productLink,
    discount,
    isFreeShip,
    hasChromeExtension,
    onClickProductDetail,
    setIsRatingModalOpen,
    isLcpCandidate = false
  } = props;
  const shopify = useAppBridge();

  const { setState, state } = React.useContext(Context);
  const navigate = useNavigate();
  const { productCount, sendLog } = state;
  const handleRefetchMyProductsCount = () => {
    const localProductCount = productCount != null && productCount >= 0 ? productCount + 1 : 0;
    setState({
      ...state,
      productCount: localProductCount,
    })
    localStorage.setItem('productCount', localProductCount);
  }
  const [importButtonLoading, setImportButtonLoading] = React.useState(false);
  const [addIsSuccess, setAddIsSuccess] = React.useState(isSaved);
  const [add_product_list_from_url] = useMutation(ADD_PRODUCT_LIST_FROM_URL);

  const handleLikeClick = (id, event) => {
    event.preventDefault();
    setImportButtonLoading(true);
    if (sendLog != null) {
      sendLog("PRODUCT_CARD_LIKE_ICON_CLICK", String(id));
    }
    if (addIsSuccess === false || isSaved === false) {
      shopify.toast.show(i18('The product is adding to My Products tab.'), {
        action: i18('View'),
        onAction: () => { navigate("/myProducts"); },
      });
      add_product_list_from_url({
        variables: {
          aliExpressProductURL: productLink
        }
      }).then((result) => {
        setImportButtonLoading(false);
        if (
          result.data != null &&
          result.data.addProductListFormURL != null
        ) {
          if (result.data.addProductListFormURL.showRatingModal === true && setIsRatingModalOpen != null) {
            setIsRatingModalOpen(true);
          }
          if (result.data.addProductListFormURL.status == false && result.data.addProductListFormURL.errorMessage != null) {
            shopify.toast.show(i18(result?.data?.addProductListFormURL?.errorMessage), {
              isError: true,
              ...(result?.data?.addProductListFormURL?.errorMessage.includes("limit") || result?.data?.addProductListFormURL?.errorMessage.includes("plan") ? {
                action: i18('View Details'),
                onAction: () => { navigate("/setting?tabShow=plan"); },
              } : {})
            });
          }
          setAddIsSuccess(result.data.addProductListFormURL.status);
          handleRefetchMyProductsCount();
        }
      })
    }
  };
  const [currentCarouselIndex, setCurrentCarouselIndex] = React.useState(0);
  const [isShowVideo, setIsShowVideo] = React.useState(false);
  const [videoCanplay, setVideoCanplay] = React.useState(false);
  const [isShowAddButton, setIsShowAddButton] = React.useState(false);
  const carouselComponent = useRef(null);
  const videoComponent = useRef(null);

  const currencySymbol = GetCurrencySymbol(currency);

  const handleChangeCarousel = (index) => {
    setCurrentCarouselIndex(index);
  }

  React.useEffect(() => {
    if (isShowVideo && videoComponent != null && videoComponent.current != null) {
      const videoElement = videoComponent.current;
      const handleLoadedMetadata = () => {
        setVideoCanplay(true);
      };

      // Add event listener for loadedmetadata
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.play?.().catch(() => { });

      // Check if the metadata has already been loaded
      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      }

      // Cleanup function to remove event listener
      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [isShowVideo]); // trigger when mouseOver
  return (
    <Box style={{
      width: cardWidth,
      height: '400px',
      position: 'relative', // For z-index stacking context
    }} onMouseOver={() => {
      setIsShowAddButton(true);
      setIsShowVideo(true);
      carouselComponent.current.goTo(1);
      setCurrentCarouselIndex(1);
    }} onMouseLeave={() => {
      setIsShowAddButton(false);
      setIsShowVideo(false);
      carouselComponent.current.goTo(0);
      setCurrentCarouselIndex(0);
    }}>
      <Box style={{
        width: cardWidth,
        height: '400px', // Fixed height - no more CLS!
        boxSizing: "border-box",
        boxShadow: "0px 2px 1px -1px #00000033, 0px 1px 1px 0px #00000024, 0px 1px 3px 0px #0000001F",
        background: "#FFF",
        borderRadius: '5px',
        zIndex: isShowAddButton ? "9" : "0",
        position: "relative",
        overflow: "hidden", // Clip content to card boundaries
        transition: 'box-shadow 0.3s ease-in-out',
      }}>
        <Box>
          <Box onClick={() => {
            if (sendLog != null) {
              sendLog("PRODUCT_CARD_IMAGE_CLICK", String(id));
            }
            // Show extension dialog ONLY when on desktop Chrome without extension installed
            const ua = navigator.userAgent || "";
            const isChromeEngine = /Chrome\/\d+/i.test(ua) && !/Edg\/|OPR\/|Brave|Chromium/i.test(ua);
            const isMobileOrTablet = /Mobile|Tablet|Android|iPad|iPhone|iPod/i.test(ua);
            const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
            const isChromeDesktop = isChromeEngine && !isMobileOrTablet && !isTouchMac;

            if (hasChromeExtension === true) {
              openExternalUrl(productLink);
            } else if (isChromeDesktop) {
              onClickProductDetail(title, productLink);
            } else {
              openExternalUrl(productLink);
            }
          }}
            sx={{
              position: 'relative',
              '& .slider-button': {
                width: '24px',
                height: '24px',
                borderRadius: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: '140px',
                cursor: "pointer",
              }
            }}
          >
            {/* Sold Badge on top of media */}
            {numberSold != null && numberSold != 0 ? (
              <Box sx={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                zIndex: 2,
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                padding: '4px 8px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
              }}>
                <Text variant="bodySm" fontWeight="medium">{numberSold} {i18("sold")}</Text>
              </Box>
            ) : null}
            <Carousel style={{ height: "280px!important" }} ref={carouselComponent} afterChange={handleChangeCarousel} fade={true} speed={300}>
              {
                parseImageList(images).map((src, index) => {
                  return <LazyLoadedCardMedia
                    src={src}
                    alt="thumbnailSource"
                    sx={{ width: cardWidth, height: "280px", borderRadius: '5px 5px 0 0', }}
                    key={index}
                    isLcpCandidate={isLcpCandidate && index === 0}
                  />
                })
              }
              {videoLink != null && videoLink.length > 0 ? <Box sx={{
                width: "270px",
                height: "270px",
                borderRadius: '15px',
                overflow: "hidden",
              }}><video
                  ref={videoComponent}
                  src={videoLink}
                  loop
                  muted
                  playsInline
                  preload="none"
                  style={{ width: '100%', height: '100%' }}
                />
              </Box> : null}
            </Carousel>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "270px",
              height: "270px",
              borderRadius: '15px',
              position: "absolute",
              top: "0px",
              left: "0px",
              background: "#FFF",
              zIndex: isShowVideo && videoLink != null && videoLink.length > 0 ? (videoCanplay ? "-1" : "1") : "-1"
            }}><Spinner size="small" /></Box>
            {parseImageList(images).length > 1 ? <Box sx={{
              left: '8.2px',
              background: 'url("/img/icon_product_slide_left.png") no-repeat 0 0',
              backgroundSize: 'cover',
              "&:hover": {
                background: 'url("/img/icon_product_slide_left_selected.png") no-repeat 0 0',
                backgroundSize: 'cover',
              }
            }} className='slider-button' onClick={(e) => {
              e.preventDefault();
              carouselComponent.current.prev();
              setCurrentCarouselIndex(currentCarouselIndex - 1);
            }} /> : <></>}
            {parseImageList(images).length > 1 ? <Box sx={{
              right: '8.2px',
              background: 'url("/img/icon_product_slide_right.png") no-repeat 0 0',
              backgroundSize: 'cover',
              "&:hover": {
                background: 'url("/img/icon_product_slide_right_selected.png") no-repeat 0 0',
                backgroundSize: 'cover',
              }
            }} className='slider-button' onClick={(e) => {
              e.preventDefault();
              carouselComponent.current.next();
              setCurrentCarouselIndex(currentCarouselIndex + 1);
            }} /> : <></>}
            <Box sx={{
              width: '87px',
              height: '87px',
              borderRadius: '50%',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              top: '159px',
              left: '148px',
              background: 'url("/img/icon_product_slide_video_player.png") no-repeat 0 0',
              backgroundSize: 'cover',
            }} onClick={(e) => {
              e.preventDefault();
            }} />
          </Box>
        </Box>
        <Box sx={{
          width: cardWidth,
          height: '100px',
          padding: "10px 12px",
          color: "#666"
        }}>
          <Box className="product-title-truncate">
            <Text variant="headingSm" >{title}</Text>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            height: '30px',
            marginBottom: '8px',
          }}>
            <Box sx={{ color: "#000" }}><Text variant="headingXl">{currencySymbol + minSalePrice}</Text></Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "20px" }}>
              {discount != "0%" && <Badge tone="critical"><Text tone="critical" variant="bodySm">{i18("Sale")}</Text></Badge >}
              {isFreeShip == true && <Badge tone="success"><Text tone="success" variant="bodySm">{i18("Free Ship")}</Text></Badge>}
            </Box>
          </Box>
        </Box>
        <Box sx={{
          width: cardWidth,
          height: '40px',
          padding: "0px 16px",
          color: '#FFF',
          position: "absolute",
          bottom: "0px", // Position at bottom of card
          left: 0,
          opacity: isShowAddButton ? 1 : 0,
          transform: isShowAddButton ? 'translateY(0)' : 'translateY(40px)', // Slide up from bottom
          pointerEvents: isShowAddButton ? 'auto' : 'none',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          zIndex: 10, // Ensure it overlaps content
          background: '#FFF', // White background
          borderRadius: '0 0 5px 5px',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // Add shadow for depth
        }}>
          {isSaved || addIsSuccess ? <Button variant="primary" disabled size="large" tone="success" fullWidth>{i18("Added to My Products")}</Button> :
            <Button variant="primary" size="large" tone="success" loading={importButtonLoading} fullWidth onClick={(event) => {
              handleLikeClick(id, event);
            }}>{i18("Add to My Products")}</Button>}
        </Box>
      </Box>
    </Box>
  );
}

function LazyLoadedCardMedia({ src, alt, sx, isLcpCandidate = false }) {
  const imageSrc = normalizeImageUrl(src);
  const parsedHeight = parseInt(sx?.height, 10);

  return (
    <DeferredMediaImage
      src={imageSrc}
      alt={alt}
      width={sx?.width || 280}
      height={Number.isFinite(parsedHeight) ? parsedHeight : 280}
      borderRadius={sx?.borderRadius || 0}
      isLcpCandidate={isLcpCandidate}
    />
  );
}

function normalizeImageUrl(url) {
  // Normalize URL - ensure it has proper https:// prefix
  if (!url) return url;
  
  // Fast path: if the URL already starts with "https://", return as-is
  if (url.startsWith("https://")) {
    return url;
  }

  // Handle common case where the URL starts with "//"
  if (url.startsWith("//")) {
    return "https:" + url;
  }

  // Handle truncated protocol prefixes (rare edge cases from data corruption)
  if (url.startsWith("ttps://")) {
    return "https://" + url.substring("ttps://".length);
  } else if (url.startsWith("tps://")) {
    return "https://" + url.substring("tps://".length);
  } else if (url.startsWith("ps://")) {
    return "https://" + url.substring("ps://".length);
  } else if (url.startsWith("s://")) {
    return "https://" + url.substring("s://".length);
  } else if (url.startsWith("://")) {
    return "https://" + url.substring("://".length);
  } else if (url.startsWith("http://")) {
    // Convert http to https
    return "https://" + url.substring("http://".length);
  }
  
  // If no valid prefix is found, prepend "https://"
  return "https://" + url;
}

